import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Buildings, Plus, X as XIcon, MagnifyingGlass, PencilSimple, CheckCircle } from "@phosphor-icons/react";
import DeleteWorkflowButton from "@/components/DeleteWorkflowButton";
import McKinseyButton from "@/components/McKinseyButton";

const COUNTRIES = ["IN", "BD", "PK", "SG", "AE", "EG", "CH", "UK", "US", "NP", "TH"];
const INDUSTRIES = ["Automotive", "Pharma", "FMCG", "Apparel & Footwear", "Electronics",
                    "Cosmetics", "Tobacco & Alcohol", "Agro-chemicals", "Software / SaaS",
                    "Luxury Goods", "Industrial Equipment", "Mixed"];

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // client being edited

  const load = () => api.get("/clients").then(({ data }) => setClients(data));
  useEffect(() => { load(); }, []);

  const filtered = clients.filter((c) =>
    !q ||
    c.name?.toLowerCase().includes(q.toLowerCase()) ||
    c.code?.toLowerCase().includes(q.toLowerCase()) ||
    c.country_code?.toLowerCase().includes(q.toLowerCase()) ||
    c.industry?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Client master</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Clients · {clients.length}</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">Brand owners SPAN represents · search by name, code, country or industry</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="hairline px-3 h-10 surface flex items-center w-full md:w-[260px]">
            <MagnifyingGlass size={12} className="text-[var(--span-text-2)] mr-2"/>
            <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search clients…" className="bg-transparent w-full text-[13px] outline-none"/>
          </div>
          <button data-testid="add-client-btn" onClick={() => setShowAdd(true)} className="span-btn">
            <Plus size={14}/> Add client
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full p-8 text-center text-[12px] text-[var(--span-text-2)] card-flat">
            {q ? `No clients match "${q}".` : "No clients yet. Click 'Add client' to create the first one."}
          </div>
        )}
        {filtered.map((c) => (
          <div key={c.client_id} className="card-flat p-5" data-testid={`client-card-${c.client_id}`}>
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 surface-2 flex items-center justify-center"><Buildings size={18}/></div>
              <div className="flex items-center gap-1">
                {c.is_primary_client && (
                  <span className="badge font-mono text-[9px] bg-emerald-50 text-emerald-800 border-emerald-200"
                    title="Verified from Client Master Sheet">
                    <CheckCircle size={9} className="inline mr-0.5" /> VERIFIED
                  </span>
                )}
                <span className="badge font-mono">{c.country_code}</span>
              </div>
            </div>
            <div className="font-display font-bold text-[16px] mt-3">{c.name}</div>
            <div className="text-[11px] text-[var(--span-text-2)] font-mono">{c.code} · {c.industry}</div>
            {c.gstin && <div className="text-[11px] font-mono mt-2 text-[var(--span-text-2)]">GSTIN {c.gstin}</div>}
            {c.billing_address && <div className="text-[11px] mt-1 text-[var(--span-text-2)]">{c.billing_address}</div>}
            {c.primary_contact && <div className="text-[11px] mt-1 text-[var(--span-text-2)]">{c.primary_contact}</div>}
            <button
              data-testid={`portal-invite-${c.client_id}`}
              onClick={async () => {
                const email = window.prompt(`Invite an external user from ${c.name} to view their cases on the SPAN portal.\nEmail:`);
                if (!email) return;
                const name = window.prompt("Name (optional)") || "";
                try {
                  await api.post(`/clients/${c.client_id}/portal-invite`, { email, name, send_email: true });
                  alert(`Invite sent to ${email}. They can now sign in and visit /portal/cases.`);
                } catch (ex) {
                  alert("Invite failed: " + (ex.response?.data?.detail || ex.message));
                }
              }}
              className="mt-3 text-[10px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)]"
              title="Invite an external client user to view their cases via the portal"
            >
              ⇲ Invite to portal
            </button>
            <div className="mt-3 flex items-center justify-between gap-2">
              {c.deleted_at && <span className="badge badge-danger text-[9px]">deleted</span>}
              <button
                onClick={() => setEditing(c)}
                data-testid={`client-edit-${c.client_id}`}
                title="Edit / add more information"
                className="hairline px-2 py-1 text-[10px] inline-flex items-center gap-1 hover:bg-[var(--span-surface-3)]">
                <PencilSimple size={11} /> Edit
              </button>
              <McKinseyButton sourceType="finance" sourceId={c.client_id}
                path={`/finance/clients/${c.client_id}/mckinsey/narrative`} label="McKinsey" />
              <div className="ml-auto">
                {!c.deleted_at && (
                  <DeleteWorkflowButton
                    entityType="client"
                    entityId={c.client_id}
                    entityLabel={c.name}
                    onRequested={load}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <AddClientModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
      {editing && (
        <EditClientModal
          client={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AddClientModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", code: "", country_code: "IN", industry: "Mixed",
    billing_address: "", primary_contact: "", gstin: "",
    email: "", phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-suggest code from name (first letters, uppercased, max 8 chars)
  useEffect(() => {
    if (form.name && !form.code) {
      const auto = form.name
        .replace(/[^a-zA-Z0-9 ]/g, "")
        .split(/\s+/).map((w) => w[0] || "")
        .join("").toUpperCase().slice(0, 8);
      setForm((f) => ({ ...f, code: auto }));
    }
  }, [form.name]);  // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.name || !form.code) {
      setErr("Name and code are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/clients", form);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid="add-client-modal">
      <form onSubmit={submit}
            className="bg-[var(--span-surface)] border border-[var(--span-border-strong)] w-full max-w-xl p-6 my-8"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow flex items-center gap-1"><Buildings size={12}/> New client</div>
          <button type="button" onClick={onClose}><XIcon size={16}/></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label-eyebrow block mb-1">Brand / Company name *</label>
            <input data-testid="client-name" required value={form.name}
                   onChange={(e) => set("name", e.target.value)}
                   placeholder="e.g. Bajaj Auto Ltd"
                   className="span-input"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Short code *</label>
              <input data-testid="client-code" required value={form.code}
                     onChange={(e) => set("code", e.target.value.toUpperCase())}
                     placeholder="BAJAJ"
                     className="span-input font-mono"/>
              <div className="text-[10px] font-mono text-[var(--span-text-2)] mt-1">Used in case codes — e.g. INV-IN-2026-{form.code || "XXX"}-001</div>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Country</label>
              <select value={form.country_code} onChange={(e) => set("country_code", e.target.value)}
                      className="span-input" data-testid="client-country">
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Industry</label>
            <select value={form.industry} onChange={(e) => set("industry", e.target.value)} className="span-input">
              {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Primary contact</label>
            <input value={form.primary_contact}
                   onChange={(e) => set("primary_contact", e.target.value)}
                   placeholder="Name + role · e.g. Rajiv Bajaj, MD"
                   className="span-input"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Contact email</label>
              <input type="email" value={form.email}
                     onChange={(e) => set("email", e.target.value)}
                     className="span-input"/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Contact phone</label>
              <input value={form.phone}
                     onChange={(e) => set("phone", e.target.value)}
                     className="span-input"/>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Billing address</label>
            <textarea value={form.billing_address}
                      onChange={(e) => set("billing_address", e.target.value)}
                      rows={2}
                      placeholder="Used on invoice headers and court bundles"
                      className="span-input"/>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">GSTIN (India clients)</label>
            <input value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())}
                   placeholder="22ABCDE1234F1Z5"
                   className="span-input font-mono"/>
          </div>
        </div>

        {err && (
          <div className="border border-[var(--span-danger)] bg-red-50 dark:bg-red-950 text-[12px] p-2 mt-3" data-testid="add-client-error">
            {String(err)}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
          <button disabled={busy} data-testid="submit-client-btn" className="span-btn">
            <Plus size={12}/> Save client
          </button>
        </div>
      </form>
    </div>
  );
}


function EditClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: client.name || "",
    code: client.code || "",
    country_code: client.country_code || "IN",
    industry: client.industry || "Mixed",
    billing_address: client.billing_address || "",
    primary_contact: client.primary_contact || "",
    gstin: client.gstin || "",
    email: client.email || "",
    phone: client.phone || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      // Only send fields that changed — smaller payload, no accidental clobbering
      const payload = {};
      for (const k of Object.keys(form)) {
        if (form[k] !== (client[k] ?? "")) payload[k] = form[k];
      }
      if (Object.keys(payload).length === 0) { onClose(); return; }
      await api.put(`/clients/${client.client_id}`, payload);
      onSaved();
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[250] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="edit-client-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} className="bg-white dark:bg-[var(--span-surface-1)] rounded shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="label-eyebrow">Edit client</div>
            <h2 className="font-display font-bold text-[20px]">{client.name}</h2>
            <div className="text-[11px] text-[var(--span-text-2)] font-mono mt-0.5">{client.client_id}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="close-edit-client">
            <XIcon size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
          <div className="md:col-span-2">
            <label className="label-eyebrow block mb-1">Client name*</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)}
              data-testid="edit-client-name" required className="span-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Code*</label>
            <input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())}
              data-testid="edit-client-code" required className="span-input font-mono" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Country</label>
            <select value={form.country_code} onChange={(e) => set("country_code", e.target.value)} className="span-input">
              {COUNTRIES.map(cc => <option key={cc} value={cc}>{cc}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Industry</label>
            <select value={form.industry} onChange={(e) => set("industry", e.target.value)} className="span-input">
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">GSTIN</label>
            <input value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())}
              data-testid="edit-client-gstin" placeholder="22ABCDE1234F1Z5" className="span-input font-mono" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Primary contact</label>
            <input value={form.primary_contact} onChange={(e) => set("primary_contact", e.target.value)}
              data-testid="edit-client-contact" placeholder="Mr. John Doe" className="span-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Email</label>
            <input value={form.email} onChange={(e) => set("email", e.target.value)}
              data-testid="edit-client-email" placeholder="contact@client.com" className="span-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Phone</label>
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
              data-testid="edit-client-phone" placeholder="+91 9876543210" className="span-input font-mono" />
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow block mb-1">Billing address</label>
            <textarea value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)}
              data-testid="edit-client-address" rows={2} placeholder="Full postal address for invoices"
              className="span-input" />
          </div>
        </div>

        {err && (
          <div className="border border-[var(--span-danger)] bg-red-50 dark:bg-red-950 text-[12px] p-2 mt-3" data-testid="edit-client-error">
            {String(err)}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
          <button disabled={busy} data-testid="submit-edit-client-btn" className="span-btn">
            <PencilSimple size={12} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
